import { formatShare, type TeamRecord } from '@clim/domain';
import { useState } from 'react';
import { ErrorBox, Field, Input, Modal, Select } from '../../components/ui';
import { useApi, useMutation } from './common';

interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  roles: { name: string }[];
}

/**
 * Chefs d'équipe : chaque équipe a un chef (superviseur, commercial ou poseur) et une part sur les
 * ventes et sur les poses de ses membres. Les membres ne voient jamais cette part : leurs montants
 * leur sont affichés nets partout.
 */
export function TeamsAdmin() {
  const teams = useApi<TeamRecord[]>('/v1/admin/teams');
  const users = useApi<UserRow[]>('/v1/admin/users');
  const [editing, setEditing] = useState<Partial<TeamRecord> | null>(null);
  const [members, setMembers] = useState<TeamRecord | null>(null);
  const active = (users.data ?? []).filter((u) => u.status === 'active');
  return (
    <div>
      <ErrorBox message={teams.error ?? users.error} />
      <div className="card">
        <div className="card-head">
          <h2>Équipes et chefs d’équipe</h2>
          <button className="btn small primary" onClick={() => setEditing({ saleShareBp: 2000, installShareBp: 0, isActive: true })}>
            + Équipe
          </button>
        </div>
        <p className="muted">
          Exemple : barème 100 € ; hors équipe, la personne touche 100 € ; membre d’une équipe à 20 %, elle touche 80 € et son chef 20 €, automatiquement. Les
          membres ne voient ni l’équipe, ni la part du chef : leurs montants sont affichés nets (catalogue, dossiers, relevés).
        </p>
        {(teams.data ?? []).map((t) => (
          <div key={t.id} className={`row legal-row${t.isActive ? '' : ' disabled'}`}>
            <div className="row-main">
              <strong>
                {t.name} — chef : {t.leaderName}
              </strong>
              <span className="muted">
                Part du chef : ventes {formatShare(t.saleShareBp)} · poses / entretiens {formatShare(t.installShareBp)}
                {t.isActive ? '' : ' · désactivée'}
              </span>
              <span className="muted">Membres : {t.members.map((m) => m.name).join(', ') || 'aucun'}</span>
            </div>
            <button className="btn small" onClick={() => setMembers(t)}>
              Membres
            </button>
            <button className="btn small" onClick={() => setEditing(t)}>
              Modifier
            </button>
          </div>
        ))}
        {!teams.data?.length && <p className="empty">Aucune équipe.</p>}
      </div>
      {editing && <TeamDialog value={editing} users={active} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), teams.reload())} />}
      {members && <MembersDialog team={members} users={active} onClose={() => setMembers(null)} onSaved={() => (setMembers(null), teams.reload())} />}
    </div>
  );
}

const pct = (bp: number | undefined) => String((bp ?? 0) / 100).replace('.', ',');
const toBp = (v: string) => Math.round(Number(v.replace(',', '.')) * 100);

function TeamDialog({ value, users, onClose, onSaved }: { value: Partial<TeamRecord>; users: UserRow[]; onClose(): void; onSaved(): void }) {
  const m = useMutation();
  const [name, setName] = useState(value.name ?? '');
  const [leaderId, setLeaderId] = useState(value.leaderId ?? '');
  const [sale, setSale] = useState(pct(value.saleShareBp));
  const [install, setInstall] = useState(pct(value.installShareBp));
  const [isActive, setIsActive] = useState(value.isActive ?? true);
  const ok = name.trim().length >= 2 && leaderId && [sale, install].every((v) => Number.isFinite(toBp(v)) && toBp(v) >= 0 && toBp(v) <= 10000);
  const save = () => {
    const body = { name, leaderId, saleShareBp: toBp(sale), installShareBp: toBp(install), isActive };
    (value.id ? m.run('PATCH', `/v1/admin/teams/${value.id}`, body) : m.run('POST', '/v1/admin/teams', body)).then(onSaved, () => undefined);
  };
  return (
    <Modal title={value.id ? 'Modifier l’équipe' : 'Nouvelle équipe'} onClose={onClose}>
      <div className="form">
        <Field label="Nom de l’équipe">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Chef d’équipe" hint="Superviseur, commercial ou poseur">
          <Select value={leaderId} onChange={(e) => setLeaderId(e.target.value)}>
            <option value="">— choisir —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName} ({u.roles.map((r) => r.name).join(' + ') || 'sans rôle'})
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid2">
          <Field label="Part du chef sur les ventes (%)">
            <Input inputMode="decimal" value={sale} onChange={(e) => setSale(e.target.value)} />
          </Field>
          <Field label="Part du chef sur les poses / entretiens (%)">
            <Input inputMode="decimal" value={install} onChange={(e) => setInstall(e.target.value)} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Équipe active
        </label>
        <p className="muted">S’applique aux commissions calculées à partir de maintenant (validation d’un dossier, fin d’une pose) ; le passé ne change pas.</p>
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={!ok} onClick={save}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MembersDialog({ team, users, onClose, onSaved }: { team: TeamRecord; users: UserRow[]; onClose(): void; onSaved(): void }) {
  const m = useMutation();
  const [ids, setIds] = useState<string[]>(team.members.map((x) => x.userId));
  const toggle = (id: string) => setIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  return (
    <Modal title={`Membres — ${team.name}`} onClose={onClose}>
      <div className="form">
        <p className="muted">Une personne n’appartient qu’à une équipe à la fois : l’ajouter ici la retire de son équipe précédente (historique conservé).</p>
        {users
          .filter((u) => u.id !== team.leaderId)
          .map((u) => (
            <label key={u.id} className="check">
              <input type="checkbox" checked={ids.includes(u.id)} onChange={() => toggle(u.id)} />
              {u.firstName} {u.lastName} <span className="muted small">({u.roles.map((r) => r.name).join(' + ') || 'sans rôle'})</span>
            </label>
          ))}
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" onClick={() => m.run('PUT', `/v1/admin/teams/${team.id}/members`, ids).then(onSaved, () => undefined)}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}
